import { useEffect, useRef, useState } from "react";
import * as Sentry from "../lib/errorReporting";
import { supabase } from "../lib/supabase";

/**
 * Manages a Supabase Realtime channel lifecycle for a caller-provided subscription.
 * Does not know about bets, legs, or game cache — purely channel plumbing.
 *
 * buildChannel and onRecover MUST be stable useCallback references at call sites.
 * Do not pass a deps array — use useCallback at the call site instead.
 *
 * Recovery behavior (Issue #119 fix):
 *   On CHANNEL_ERROR or TIMED_OUT:
 *     1. Sentry: attempt 0-1 console.warn only; attempt 2 captureMessage; attempt 3+ captureException
 *     2. call onRecover(reason, meta)
 *     3. supabase.removeChannel() the broken channel
 *     4. schedule resubscribe with bounded exponential backoff:
 *        initialMs=1000, factor=2, maxMs=30000, jitterMs=250
 *   On SUBSCRIBED: clear timer, reset attempt count, call onSubscribed
 *   On cleanup: set isCleaningUpRef=true, clear timer, remove channel.
 *     CLOSED triggered by cleanup must NOT trigger reconnect.
 *     Stale channel callbacks must be ignored after cleanup or channel replacement.
 *
 * @param {Object} params
 * @param {boolean} params.enabled - whether channel should be active
 * @param {string} params.channelName - e.g. "bet-legs"
 * @param {{ function: string, channel: string }} params.sentryContext - Sentry tags
 * @param {Function} params.buildChannel - useCallback(ch => ch.on(...)) — attaches handlers, returns channel
 * @param {Function} params.onRecover - useCallback((reason, { attempt, delayMs, status }) => void)
 * @param {Function} [params.onSubscribed] - optional useCallback(() => void)
 * @param {string[]} [params.recoverOnStatuses] - default: ["CHANNEL_ERROR", "TIMED_OUT"]
 * @returns {{ status: string, reconnectAttempt: number, lastError: Error|null, reconnectNow: Function }}
 */
export function useRealtimeChannel({
  enabled,
  channelName,
  sentryContext,
  buildChannel,
  onRecover,
  onSubscribed,
  recoverOnStatuses = ["CHANNEL_ERROR", "TIMED_OUT"],
}) {
  const [status, setStatus] = useState("CLOSED");
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [lastError, setLastError] = useState(null);

  // Mutable refs — survive re-renders without triggering effect re-runs
  const isCleaningUpRef = useRef(false);
  const attemptRef = useRef(0);
  const timerRef = useRef(null);
  const reconnectNowRef = useRef(() => {});

  // Keep array/object params in refs so they can't cause spurious effect re-runs
  const recoverOnStatusesRef = useRef(recoverOnStatuses);
  recoverOnStatusesRef.current = recoverOnStatuses;
  const sentryContextRef = useRef(sentryContext);
  sentryContextRef.current = sentryContext;

  useEffect(() => {
    if (!supabase || !enabled) return;

    isCleaningUpRef.current = false;
    attemptRef.current = 0;

    // currentChannel is local to this effect invocation; captured by closures below.
    // When a channel is replaced during reconnect, the old subscribe callback
    // detects staleness via `ch !== currentChannel`.
    let currentChannel = null;

    function clearTimer() {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    function subscribe() {
      if (isCleaningUpRef.current) return;

      const ch = supabase.channel(channelName);
      currentChannel = buildChannel(ch);

      currentChannel.subscribe((subStatus) => {
        // Stale callback: this channel was already replaced or removed
        if (ch !== currentChannel) return;
        // Cleanup-triggered CLOSED must NOT reconnect
        if (isCleaningUpRef.current && subStatus === "CLOSED") return;

        setStatus(subStatus);

        if (subStatus === "SUBSCRIBED") {
          clearTimer();
          attemptRef.current = 0;
          setReconnectAttempt(0);
          setLastError(null);
          if (onSubscribed) onSubscribed();
          return;
        }

        if (recoverOnStatusesRef.current.includes(subStatus)) {
          const attempt = attemptRef.current;
          const initialMs = 1000;
          const factor = 2;
          const maxMs = 30000;
          const jitterMs = 250;
          const delayMs =
            Math.min(initialMs * Math.pow(factor, attempt), maxMs) +
            Math.random() * jitterMs;

          const reason = subStatus === "CHANNEL_ERROR" ? "channel-error" : "timed-out";
          const err = new Error(`${channelName} ${reason}`);
          if (attempt >= 3) {
            Sentry.captureException(err, {
              tags: { ...sentryContextRef.current, attempt },
            });
          } else if (attempt >= 2) {
            Sentry.captureMessage(`${channelName} ${reason} (attempt ${attempt})`, {
              level: "warning",
              tags: { ...sentryContextRef.current, attempt },
            });
          } else {
            console.warn(`[${channelName}] ${reason} (attempt ${attempt}) — self-resolving, skipping Sentry`);
          }
          setLastError(err);

          if (onRecover) {
            onRecover(reason, { attempt, delayMs, status: subStatus });
          }

          // Remove broken channel before scheduling reconnect
          supabase.removeChannel(currentChannel);
          currentChannel = null;

          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (!isCleaningUpRef.current) {
              attemptRef.current += 1;
              setReconnectAttempt((a) => a + 1);
              subscribe();
            }
          }, delayMs);
        }
      });
    }

    reconnectNowRef.current = () => {
      clearTimer();
      if (currentChannel) {
        supabase.removeChannel(currentChannel);
        currentChannel = null;
      }
      attemptRef.current = 0;
      if (onRecover) {
        onRecover('manual-reconnect', { attempt: 0, delayMs: 0, status: 'manual' });
      }
      subscribe();
    };

    subscribe();

    return () => {
      isCleaningUpRef.current = true;
      clearTimer();
      if (currentChannel) {
        supabase.removeChannel(currentChannel);
        currentChannel = null;
      }
    };
  }, [enabled, channelName, buildChannel, onRecover, onSubscribed]);

  return {
    status,
    reconnectAttempt,
    lastError,
    reconnectNow: () => reconnectNowRef.current(),
  };
}
